<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import countryPhoneCodes from '../../../common/countryPhoneCodes.json';
import ChevronRightIcon from '@/assets/icons/ChevronRightIcon.vue';
import MagnifyingGlass from '@/assets/icons/MagnifyingGlass.vue';

interface DropdownOption {
  title: string;
  code: string;
}

const props = defineProps<{
  dropdownValue: DropdownOption;
  dropdownOptions: Array<DropdownOption>;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:dropdownValue', value: DropdownOption): void;
}>();

const dropdownValue = computed({
  get() {
    return props.dropdownValue;
  },
  set(value) {
    emit('update:dropdownValue', value);
  },
});

function getIcon(code: string): string | undefined {
  return countryPhoneCodes.find((i: { code: string; icon: string }) => i.code == code)?.icon;
}

const searchQuery = ref<string>('');
const isDropdownOpen = ref<boolean>(false);

const filteredOptions = computed(() =>
  props.dropdownOptions.filter((option) =>
    option.title.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
);

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
  searchQuery.value = '';
}

function selectOption(option: DropdownOption) {
  emit('update:dropdownValue', option);
  isDropdownOpen.value = false;
}

const dropdownRef = ref<HTMLElement | null>(null);

function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});
</script>

<template>
  <div
    class="dark:border-dark-semi-transparent border-light-border dark:bg-dark-list-bg flex h-12 cursor-pointer items-center rounded-lg border"
  >
    <div class="relative w-full" ref="dropdownRef">
      <button
        data-test="region-dropdown-button"
        @click="toggleDropdown"
        class="relative flex w-full cursor-pointer items-center rounded-md py-2 pr-5 pl-3 text-left dark:shadow-md"
      >
        <span
          v-if="dropdownValue.code !== ''"
          :aria-hidden="!dropdownValue.code"
          :class="['text-light-primary dark:text-dark-list-text mr-3 block text-base']"
          >{{ getIcon(dropdownValue.code) || ' ' }}</span
        >

        <span
          :class="[
            dropdownValue.code === ''
              ? ''
              : 'dark:border-dark-semi-transparent border-light-border border-l',
            'text-light-primary dark:text-dark-list-text box-border flex w-full bg-transparent pl-4 text-base outline-none',
          ]"
          data-test="selected-option-display"
        >
          {{ dropdownValue.title || 'Select a country' }}
        </span>
        <span class="pointer-events-none items-center">
          <ChevronRightIcon class="w-4 rotate-90" />
        </span>
      </button>
      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-2"
      >
        <div
          v-if="isDropdownOpen"
          class="bg-light-list-bg text-light-primary ring-light-primary/5 dark:bg-dark-list-bg dark:text-dark-secondary dark:ring-dark-black absolute top-14 z-10 max-h-60 w-full overflow-x-clip overflow-y-auto rounded-md pt-1 text-base shadow-lg focus:outline-none sm:text-sm"
        >
          <div class="flex items-center">
            <span
              class="relative flex max-w-xs overflow-x-clip rounded px-4 py-2 select-none sm:max-w-full"
            >
              <MagnifyingGlass class="w-4" />
            </span>
            <input
              data-test="region-dropdown-search-input"
              v-model="searchQuery"
              class="w-full bg-inherit pr-2 focus:outline-none"
              type="text"
              placeholder="Search country"
            />
          </div>

          <ul data-test="region-dropdown-list" class="max-h-60">
            <li
              data-test="region-dropdown-option"
              v-for="option in filteredOptions"
              :key="option.code"
              @click="selectOption(option)"
              :class="[
                option.code === dropdownValue.code
                  ? 'bg-light-select dark:bg-dark-black'
                  : 'font-normal',
                'dark:hover:bg-dark-black flex cursor-pointer px-4 py-2 hover:bg-slate-50',
              ]"
            >
              <span :class="['pr-4']">{{ getIcon(option.code) }}</span>
              <span
                :class="[
                  option.code === dropdownValue.code ? 'font-bold' : 'font-normal',
                  'block truncate',
                ]"
              >
                {{ option.title }}
              </span>
            </li>
            <li
              data-test="unsupported-country-message"
              v-if="filteredOptions.length === 0"
              class="dark:border-dark-semi-transparent border-light-border bg-light-black text-light-list-text mx-2 my-2 rounded-md border px-2 py-2 text-center"
            >
              If you can't see your country, it isn't currently supported.
            </li>
          </ul>
        </div>
      </transition>
    </div>
  </div>
</template>
